import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  useEffect(() => {
    const checkFirstLogin = async () => {
      try {
        console.log('Checking first login...');
        const response = await api.get('/auth/check-first-login');
        console.log('First login check response:', response.data);
        setIsFirstLogin(response.data.isFirstLogin);
      } catch (error) {
        console.error('Error checking first login:', error);
        setIsFirstLogin(true);
      }
    };
    checkFirstLogin();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Form submission started');
      console.log('Form data:', formData);
      console.log('Is first login?', isFirstLogin);

      let response;
      if (isFirstLogin) {
        console.log('Attempting to create first admin user...');
        response = await api.post('/auth/create-first-admin', {
          username: formData.identifier,
          password: formData.password,
          email: formData.identifier,
          name: formData.identifier
        });
        console.log('First admin creation successful:', response.data);
      } else {
        console.log('Attempting normal login...');
        response = await api.post('/auth/login', {
          username: formData.identifier,
          password: formData.password
        });
        console.log('Login successful:', response.data);
      }

      const { token, user } = response.data;
      console.log('Received user data:', user);

      localStorage.setItem('token', token.startsWith('Bearer ') ? token : `Bearer ${token}`);
      localStorage.setItem('user', JSON.stringify(user));
      console.log('Stored token and user data in localStorage');

      setFormData({ identifier: '', password: '' });

      console.log('Navigating to dashboard...');
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/operator/dashboard');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      setError(
        error.response?.data?.message ||
        error.response?.data?.error ||
        t('login.errors.invalidCredentials')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-2xl font-bold mb-2">
            {isFirstLogin ? t('login.title.firstLogin') : t('login.title.normal')}
          </h2>
          <p className="text-center text-xs text-gray-500">{t('login.version')}</p>
          {isFirstLogin && (
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('login.firstLoginMessage')}
            </p>
          )}
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="identifier" className="sr-only">
                {isFirstLogin ? t('login.form.adminUsername') : t('login.form.username')}
              </label>
              <input
                id="identifier"
                name="identifier"
                type="text"
                required
                value={formData.identifier}
                onChange={handleChange}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={isFirstLogin ? t('login.form.adminUsername') : t('login.form.username')}
                autoComplete="username"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                {t('login.form.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={t('login.form.password')}
                autoComplete="current-password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? t('login.actions.processing') : (isFirstLogin ? t('login.actions.createAdmin') : t('login.actions.signIn'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 